import { useId, type ReactElement, type ReactNode } from 'react';
import { Box, Heading, Image, Text, type BoxProps, type HeadingProps, type TextProps } from '@chakra-ui/react';
import type { Icon } from '@phosphor-icons/react';
import { HARD_SHADOW } from '@/travo/theme/constants';

/** Per-card art that sits beside localized copy: an icon, a theme color token, a slight tilt. */
export interface CardArt {
  icon: Icon;
  /** Theme color token for the icon chip or stamp edge. */
  color: string;
  /** CSS angle, e.g. '-1deg'. */
  tilt: string;
}

/** Shrikhand display heading; CJK glyphs fall back to Noto Sans TC Black. '\n' breaks lines. */
export const DisplayHeading = (props: HeadingProps) => (
  <Heading
    as="h2"
    className="display"
    fontFamily="heading"
    fontWeight="900"
    lineHeight="1.15"
    letterSpacing="normal"
    whiteSpace="pre-line"
    // CJK: break at spaces/punctuation, not mid-phrase; still wrap anywhere rather than overflow
    wordBreak="keep-all"
    overflowWrap="anywhere"
    color="inherit"
    {...props}
  />
);

interface CardTitleProps {
  children: ReactNode;
}

/** Title inside a stamp card; balanced so a two-line CJK title doesn't leave one character alone. */
export const CardTitle = ({ children }: CardTitleProps): ReactElement => (
  <Heading as="h3" fontFamily="body" fontSize="22px" lineHeight="1.3" fontWeight="700" css={{ textWrap: 'balance' }}>
    {children}
  </Heading>
);

/** Small mono label above a heading, like a postmark's printed line. */
export const Eyebrow = (props: TextProps) => (
  <Text fontFamily="mono" fontSize="13px" letterSpacing="0.14em" textTransform="uppercase" color="muted" {...props} />
);

interface DotPillProps {
  children: ReactNode;
}

/** Bordered pill with a sun dot. */
export const DotPill = ({ children }: DotPillProps) => (
  <Box
    display="inline-flex"
    alignSelf="flex-start"
    alignItems="center"
    gap="10px"
    fontFamily="mono"
    fontSize="13px"
    letterSpacing="0.14em"
    textTransform="uppercase"
    border="2px solid"
    borderColor="ink"
    borderRadius="full"
    px="14px"
    py="6px"
    bg="card"
  >
    <Box w="8px" h="8px" borderRadius="full" bg="sun" />
    {children}
  </Box>
);

interface StampProps extends BoxProps {
  /** Color of the perforated margin. */
  edge?: string;
  /** Background inside the inked frame. */
  frame?: string;
  frameBorder?: string;
  margin?: BoxProps['p'];
  innerPadding?: BoxProps['p'];
}

/** Perforated postage stamp: edge margin, inked frame, content. */
export const Stamp = ({
  edge = 'card',
  frame = 'transparent',
  frameBorder = 'ink',
  margin = '16px',
  innerPadding = '22px',
  children,
  ...rest
}: StampProps) => (
  <Box filter={HARD_SHADOW.stamp} {...rest}>
    <Box className="perf" bg={edge} p={margin} h="100%">
      <Box border="2px solid" borderColor={frameBorder} bg={frame} p={innerPadding} h="100%">
        {children}
      </Box>
    </Box>
  </Box>
);

interface PostmarkProps extends Omit<BoxProps, 'children'> {
  /** Text running around the ring (~34 characters fills it). */
  ring: string;
  /** Two small mono lines in the center, e.g. date and year. */
  label?: string;
  sub?: string;
  /** One big display word in the center instead of label/sub. */
  big?: string;
  dashed?: boolean;
  /** Accessible name; omit for a decorative mark. */
  title?: string;
}

// Inline styles, not SVG attributes: global CSS outranks presentation attributes and reset the fonts.
// Font families are the theme's font tokens as CSS variables.
const MONO_FONT = 'var(--chakra-fonts-mono)';
const RING_TEXT = { fontFamily: MONO_FONT, fontSize: 13, letterSpacing: 3.2 } as const;
const BIG_TEXT = { fontFamily: 'var(--chakra-fonts-heading)', fontSize: 32, fontWeight: 400 } as const;
const LABEL_TEXT = { fontFamily: MONO_FONT, fontSize: 17, fontWeight: 500 } as const;
const SUB_TEXT = { fontFamily: MONO_FONT, fontSize: 15 } as const;

/** Circular cancellation postmark, drawn in currentColor. */
export const Postmark = ({ ring, label, sub, big, dashed, title, ...rest }: PostmarkProps) => {
  const pathId = `pm-${useId().replace(/[^\w-]/g, '')}`;
  return (
    <Box {...rest}>
      <svg
        viewBox="0 0 180 180"
        width="100%"
        height="100%"
        {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
      >
        <defs>
          <path id={pathId} d="M90,90 m-63,0 a63,63 0 1,1 126,0 a63,63 0 1,1 -126,0" />
        </defs>
        <circle
          cx="90"
          cy="90"
          r="82"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray={dashed ? '6 4' : undefined}
        />
        <circle cx="90" cy="90" r="48" fill="none" stroke="currentColor" strokeWidth="2" />
        <text style={RING_TEXT} fill="currentColor">
          <textPath href={`#${pathId}`}>{ring}</textPath>
        </text>
        {big ? (
          <text x="90" y="100" textAnchor="middle" style={BIG_TEXT} fill="currentColor">
            {big}
          </text>
        ) : (
          <>
            <text x="90" y="86" textAnchor="middle" style={LABEL_TEXT} fill="currentColor">
              {label}
            </text>
            <text x="90" y="108" textAnchor="middle" style={SUB_TEXT} fill="currentColor">
              {sub}
            </text>
          </>
        )}
      </svg>
    </Box>
  );
};

const WAVE_ROWS = [8, 22, 36, 50];
const wavePath = (y: number): string =>
  `M0 ${y} Q8 ${y - 8} 16 ${y} ` + Array.from({ length: 15 }, (_, i) => `T${32 + 16 * i} ${y}`).join(' ');

/** Wavy cancellation lines that run off a postmark. */
export const CancelWaves = (props: BoxProps) => (
  <Box {...props}>
    <svg viewBox="0 0 260 60" width="100%" height="100%" aria-hidden>
      <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        {WAVE_ROWS.map((y) => (
          <path key={y} d={wavePath(y)} />
        ))}
      </g>
    </svg>
  </Box>
);

interface DotListProps {
  items: string[];
  /** Theme color token for the bullet dots. */
  dot: string;
}

/** A dot-led bullet list used in the passport and plan sections. */
export const DotList = ({ items, dot }: DotListProps) => (
  <Box as="ul" listStyleType="none" display="flex" flexDirection="column" gap="12px">
    {items.map((item) => (
      <Box as="li" key={item} display="flex" gap="12px" alignItems="flex-start">
        <Box flex="none" w="10px" h="10px" mt="9px" borderRadius="full" bg={dot} border="2px solid" borderColor="ink" />
        <Text as="span">{item}</Text>
      </Box>
    ))}
  </Box>
);

interface PhoneFrameProps extends Omit<BoxProps, 'children'> {
  /** A raw app screenshot (status bar included, no device frame). */
  src: string;
  alt: string;
  /** The screenshot's width / height (e.g. '640 / 1387'), so the frame reserves its space before it loads. */
  ratio: string;
  /** Above-the-fold LCP image: fetch first. Otherwise it lazy-loads. */
  priority?: boolean;
}

/** Ink phone bezel around a raw app screenshot; size and position it like a Box. */
export const PhoneFrame = ({ src, alt, ratio, priority = false, ...rest }: PhoneFrameProps): ReactElement => (
  <Box bg="ink" p={{ base: '6px', md: '8px' }} borderRadius={{ base: '30px', md: '40px' }} {...rest}>
    <Image
      src={src}
      alt={alt}
      display="block"
      w="100%"
      aspectRatio={ratio}
      borderRadius={{ base: '24px', md: '32px' }}
      {...(priority ? { fetchPriority: 'high' as const } : { loading: 'lazy' as const })}
    />
  </Box>
);

/** Centered page column. */
export const Container = (props: BoxProps) => <Box maxW="1240px" mx="auto" px={{ base: 4, md: 6 }} {...props} />;
